import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import PaymentInquiry from '../../../../services/FamilyPlaza/PaymentInquiry';
import { requireAuthentication } from '../../../../utils/authUtils';
import FamilyPlazaTopSection from '../../../../components/family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from '../../../../components/family-plaza/FamilyPlazaSubHeading';

const ManagementFeePaymentInquiryPC = () => {
  const navigate = useNavigate();

  // Navigation with authentication check
  const handleNavigation = (route) => {
    if (!requireAuthentication()) return;
    navigate(route);
  };

  // Form input states
  const [searchType, setSearchType] = useState('deceased');
  const [searchValue, setSearchValue] = useState('');
  const [sealNumber, setSealNumber] = useState('');
  const [byeolbitgwanSearchValue, setByeolbitgwanSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Results states
  const [searchResults, setSearchResults] = useState([]);
  const [showBasicResults, setShowBasicResults] = useState(false);

  const [byeolbitgwanResults, setByeolbitgwanResults] = useState(null);
  const [showByeolbitgwanResults, setShowByeolbitgwanResults] = useState(false);

  // ===== Existing Contractor Search =====
  const handleExistingContractorSearch = async () => {
    if (!searchValue.trim()) {
      alert('조회 정보를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params =
        searchType === 'deceased'
          ? { deceasedName: searchValue }
          : { contractorName: searchValue };

      const response = await PaymentInquiry.getManagementFeeSearch(params);

      if (Array.isArray(response) && response.length > 0) {
        const allResults = response.map(result => ({
          goinId: result.goinId,
          deceasedName: result.goinName,
          enshrineDay: result.enshrineDay,
          contractorName: result.customerName,
          customerBirthday: result.customerBirthday,
          deathDate: result.deathDate,
          fullResult: result,
        }));

        setSearchResults(allResults);
        setShowBasicResults(true);
      } else {
        setSearchResults([]);
        setShowBasicResults(false);
        setError('검색 결과가 없습니다.');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
      setShowBasicResults(false);
    } finally {
      setLoading(false);
    }
  };

  // ===== Navigate to Detailed Results =====
  const handleDetailedSearch = (result) => {
    if (!result) {
      alert('조회 정보를 입력해주세요.');
      return;
    }

    const goinId = result.fullResult?.goinId;
    const customerName = result.fullResult?.customerName;
    const birthday = result.fullResult?.customerBirthday;
    navigate('/management-fee-inquiry-results', {
      state: { goinId, customerName, birthday }
    });
  };

  // ===== Byeolbitgwan Mode2 Search =====
  const handleByeolbitgwanSearch = async () => {
    if (!sealNumber.trim()) {
      alert('조회 정보를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Mode2 API call
      const response = await PaymentInquiry.getMode2FeeSearch({
        deceasedName: byeolbitgwanSearchValue,
        enshrineName: sealNumber
      });

      if (response && response.length > 0) {
        const basicInfo = response[0];
        const paymentHistory = response.slice(1);

        const apiData = {
          id: basicInfo.id || '',
          deceasedName: basicInfo.deceasedName || '',
          contractorName: basicInfo.customerName || '',
          enshrineUnit: basicInfo.enshrineName || '',
          firstEnshrineDate: basicInfo.enshrineDay || '',
          birthday: basicInfo.birthday || '',
          address: basicInfo.address || '',
          phoneHome: basicInfo.phoneHome || '',
          phoneCell: basicInfo.phoneCell || '',
          paymentHistory: paymentHistory.map(item => ({
            startEndDay: item.startEndDay || '',
            receiptPrice: item.receiptPrice || '',
            paymentStatus: item.paymentStatus || '',
            receiptDay: item.receiptDay || '',
            enshrineName: item.enshrineName || ''
          }))
        };

        setByeolbitgwanResults(apiData);
        setShowByeolbitgwanResults(true);

        // localStorage.setItem('managementFeeSearchResults', JSON.stringify(apiData));

        setSearchResults(null);
        setShowBasicResults(false);
      } else {
        alert('조회 결과가 없습니다.');
        setByeolbitgwanResults(null);
        setShowByeolbitgwanResults(false);
      }
    } catch (err) {
      console.error(err);
      alert('조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // ===== Render =====
  return (
    <div className="management-fee-payment-inquiry-pc page-management-fee-payment-inquiry-pc announcement-list-page cyber-info-page-pc">
      <FamilyPlazaTopSection />

      <main className="main-content">
        <FamilyPlazaSubHeading heading="관리비 납부 조회" />

        <div className="management-fee-payment-inquiry-main-content">
          <div className="management-fee-payment-inquiry-page-content">
            <div className="management-fee-payment-inquiry-container">

              <div className="management-fee-payment-inquiry-content-area">

                {/* Intro */}
                <div className="management-fee-payment-inquiry-intro">
                  <p>간편한 강화파라다이스만의 관리비 납부 조회입니다. 간단한 확인 절차만으로 묘역의 관리비 납부 조회가 가능합니다.</p>
                  <p className="management-fee-payment-inquiry-intro-bold">아래의 검색창에서 관리비 납부 조회를 해주세요.</p>
                </div>

                {/* Forms */}
                <div className="management-fee-payment-inquiry-forms">

                  {/* Existing Contractor */}
                  <div className="management-fee-payment-inquiry-section">
                    <h3 className="management-fee-payment-inquiry-section-title">기존 계약자 조회</h3>
                    <div className="management-fee-payment-inquiry-form">
                      <div className="management-fee-payment-inquiry-input-group">
                        <div className="management-fee-payment-inquiry-dropdown">
                          <select 
                            value={searchType} 
                            onChange={(e) => {
                              setSearchType(e.target.value);
                              setSearchResults([]);
                              setShowBasicResults(false);
                              setError(null);
                            }}
                          >
                            <option value="deceased">고인명</option>
                            <option value="contractor">계약자명</option>
                          </select>
                        </div>
                        <div className="management-fee-payment-inquiry-text-input">
                          <input
                            type="text"
                            placeholder="고인명 또는 계약자명 입력"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                          />
                        </div>
                        <button
                          className="management-fee-payment-inquiry-search-btn"
                          onClick={handleExistingContractorSearch}
                          disabled={loading}
                        >
                          {loading ? '검색중...' : '검색'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Byeolbitgwan Mode2 */}
                  <div className="management-fee-payment-inquiry-section">
                    <h3 className="management-fee-payment-inquiry-section-title">별빛관 전용</h3>
                    <div className="management-fee-payment-inquiry-form">
                      <div className="management-fee-payment-inquiry-input-group">
                        <div className="management-fee-payment-inquiry-seal-input">
                          <input
                            type="text"
                            placeholder="봉인 번호 입력 (예: 별빛관-000)"
                            value={sealNumber}
                            onChange={(e) => setSealNumber(e.target.value)}
                          />
                        </div>
                        <div className="management-fee-payment-inquiry-text-input">
                          <input
                            type="text"
                            placeholder="고인명, 계약자명 또는 주민번호 앞자리 (예: 홍길동, 77120)"
                            value={byeolbitgwanSearchValue}
                            onChange={(e) => setByeolbitgwanSearchValue(e.target.value)}
                          />
                        </div>
                        <button
                          className="management-fee-payment-inquiry-search-btn"
                          onClick={handleByeolbitgwanSearch}
                          disabled={loading}
                        >
                          {loading ? '검색중...' : '검색'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {error && <div className="management-fee-payment-inquiry-error"><p>{error}</p></div>}

                </div>

                {/* Results: Existing Contractor */}
                {showBasicResults && searchResults && searchResults.length > 0 && (
                  <div className="management-fee-payment-inquiry-results">
                    <table className="cyber-memorial-search-table cyber-memorial-search-pc-table-unique">
                      <tbody>
                        {searchResults.map((result, index) => (
                          <tr key={index}>
                            <td>故 {result.deceasedName}</td>
                            <td>{result.enshrineDay} 안치</td>
                            <td>{result.contractorName} 계약</td>
                            <td>
                              <button
                                className="cyber-memorial-search-pc-record-search-btn"
                                onClick={() => handleDetailedSearch(result)}
                                disabled={loading}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Results: Byeolbitgwan Mode2 */}
                {showByeolbitgwanResults && byeolbitgwanResults && (
                  <div className="management-fee-payment-inquiry-results">
                    <table className="cyber-memorial-search-table cyber-memorial-search-pc-table-unique">
                      <tbody>
                        <tr>
                          <td>故 {byeolbitgwanResults.deceasedName}</td>
                          <td>{byeolbitgwanResults.firstEnshrineDate} 안치</td>
                          <td>{byeolbitgwanResults.contractorName} 계약</td>
                          <td>
                            <button
                              className="cyber-memorial-search-pc-record-search-btn"
                              onClick={() =>
                                navigate('/management-fee-inquiry-results', {
                                  state: {
                                    deceasedName: byeolbitgwanResults.deceasedName,
                                    enshrineName: byeolbitgwanResults.enshrineUnit,
                                    birthdayMode2: byeolbitgwanResults.birthday,
                                    goinIdMode2: byeolbitgwanResults.id,
                                    customerNameMode2: byeolbitgwanResults.contractorName,
                                  }
                                })
                              }
                              disabled={loading}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Bottom Info */}
                <div className="management-fee-payment-inquiry-info">
                  <div className="management-fee-payment-inquiry-info-content">
                    <div className="management-fee-payment-inquiry-info-icon">!</div>
                    <div className="management-fee-payment-inquiry-info-text">
                      <p>로그인 후 이용 가능합니다.</p>
                      <p>*조회가 안되는 회원분께서는 관리사무실 <span className="management-fee-payment-inquiry-phone">1599-9442</span>로 연락바랍니다.</p>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManagementFeePaymentInquiryPC;

